<template>
  <div class="koukyuu">
    <h3>公休</h3>
    <div class="multi-date-table" v-for="(item, index) in koukyuu.koukyuuList" :key="index">
      <div class="head"><b>公休{{ (index + 1) }}</b></div>

      <div class="item date">
        <b class="required">取得日</b>
        <date-picker v-model="item.koukyuu_date" :error="getError(item, 'koukyuu_date')"
          @update:modelValue="changeData"></date-picker>
      </div>

      <div class="item">
      <koyouKeiyakuTimeSelect :errors="item.errors" :targetDate="item.koukyuu_date"
        @change="changeKoyouKeiyakuTime(index, $event)" :hideWorkTime="true">
      </koyouKeiyakuTimeSelect>
      </div>
      
      <div class="item kubun" v-if="item.selectedSyuugyouBasyo">
        <b class="required">区分</b>
        <select-box :options="item.selectableTaniTypes" v-model="item.koukyuu_tani_type" @update:modelValue="changeData"
          :error="getError(koukyuu, 'koukyuu_tani_type')"></select-box>
      </div>

      <div class="row-button">
        <i class="fa-solid fa-delete-left" @click="deleteRow(index)" v-if="(koukyuu.koukyuuList.length > 1)"></i>
      </div>
    </div>
    <p class="item btn-table-add">
      <button type="button" class="btn primary" @click="addRow">
        <i class="fa-solid fa-plus"></i>追加
      </button>
    </p>
  </div>
</template>

<script lang="ts">

import { defineComponent, ref, Ref, onMounted } from 'vue';
import { useInit } from '@/composable/shinsei/koukyuu/init';
import { useCreate } from '@/composable/shinsei/koukyuu/create';
import { useForm } from '@/composable/form/form';
import { KoyouKeiyakuTimeSelectEmitData, SyuugyouBasyoAndKoyouKeiyakuOption } from '@/types/SyuugyouBasyo';
import { dateFormat } from '@/filters/common';
import { useTableRow } from "@/composable/common/tablerow";
import OptionConst from '@/const/Option';
import { DialogService } from '@/service/DialogService';
import KoukyuuNotAll from './koukyuu_parts/KoukyuuNotAll.vue';
import KoukyuuAll from './koukyuu_parts/KoukyuuAll.vue';
import { Logger } from '@/utils/Logger';
import KoyouKeiyakuTimeSelect from "@/components/shinsei/create/parts/KoyouKeiyakuTimeSelect.vue"

export default defineComponent({
  props: {
    shinseiDate: {
      type: String,
      default: null
    },
    selectSyuugyouBasyo: {
      type: Object as () => SyuugyouBasyoAndKoyouKeiyakuOption,
      default: null
    },
  },
  setup(props, context) {
    // エラー取得
    const { getError } = useForm()
    const { koukyuu } = useInit()
    const { create } = useCreate()
    const { addRowNoRef, removeTableRow } = useTableRow();
    const confData: Ref<string[]> = ref([])

    onMounted(async () => {
      Logger.debug('----------------------------\n公休申請コンポーネントの初期化開始')
      Logger.debug('公休申請 onMounted', { '申請': koukyuu })
    })


    const changeData = () => {
      confData.value = []
      koukyuu.koukyuuList.forEach((v, i) => {
        const tani = OptionConst.SHINSEI_DIV_OPTION.find(val => val.val == v.koukyuu_tani_type)?.label ?? '未指定'
        const date = v.koukyuu_date ?? '未指定'
        const idx = i + 1
        const item = idx + '：　' + date + '(' + tani + ')'
        confData.value.push(item)
      })
    }

    // １日公休、AM公休、PM公休のすべてのセレクトオプションを表示するかどうか
    const isAll = async (syuugyouBasyo: SyuugyouBasyoAndKoyouKeiyakuOption) => {

      // パートかどうか
      const isPart = syuugyouBasyo.keitai_type == OptionConst.KOYOU_KEITAI_TYPE.HAKENSYAIN_PART
        || syuugyouBasyo.keitai_type == OptionConst.KOYOU_KEITAI_TYPE.KEIYAKUSYAIN_PART


      // const sashihikiTimes = DateUtil.timeToDate(data.sashihiki_times ?? '')

      // パートでなく、かつ、差引時間が無い場合
      //（運用上、差し引き時間が設定されている方は、半日取得対象でないことより）
      return !isPart && (syuugyouBasyo.sashihiki_times == '00:00:00')
    }

    const getShinseiOption = async (syuugyouBasyo: SyuugyouBasyoAndKoyouKeiyakuOption) => {
      if (await isAll(syuugyouBasyo)) {
        return OptionConst.SHINSEI_DIV_OPTION
      } else {
        return OptionConst.SHINSEI_DIV_OPTION.filter((v) => v.val === OptionConst.SHINSEI_DIV.FULL_DAY)
      }
    }

    const deleteRow = async (index: number) => {
      await removeTableRow(koukyuu.koukyuuList, index)
      changeData()
    }

    const onSave = () => {
      const htmlText = '<p>' + confData.value.join('<br>') + '</p>'
      DialogService.question({
        html: '<p style="margin-bottom:10px;">以下を公休として申請しますか？</p>' + htmlText,
        showCancelButton: true,
        cancelButtonText: 'いいえ',
        confirmButtonText: 'はい'
      }).then(async (result) => {
        if (result.isConfirmed) {
          create(koukyuu)
        }
      })
    }

    // 行追加
    const addRow = () => {
      const row = {
        koukyuu_date: null,
        koukyuu_tani_type: null
      }
      addRowNoRef(koukyuu.koukyuuList, row)
    }

    const changeKoyouKeiyakuTime = async (index: number, emitData: KoyouKeiyakuTimeSelectEmitData) => {
      if (emitData.selectedSyuugyouBasyo) {
        koukyuu.koukyuuList[index].koyou_keiyaku_id = emitData.selectedSyuugyouBasyo.koyou_id
        koukyuu.koukyuuList[index].mst_syuugyou_basyo_id = emitData.selectedSyuugyouBasyo.val
        koukyuu.koukyuuList[index].selectedSyuugyouBasyo = emitData.selectedSyuugyouBasyo
        koukyuu.koukyuuList[index].selectableTaniTypes = await getShinseiOption(emitData.selectedSyuugyouBasyo)
        koukyuu.koukyuuList[index].koukyuu_tani_type = typeof koukyuu.koukyuuList[index].selectableTaniTypes != 'undefined' ? koukyuu.koukyuuList[index].selectableTaniTypes![0].val : null // ビルドエラー対策のため型チェック厳格め
        changeData()
        Logger.debug('公休' + index + 'に雇用契約IDと就業場所IDをセットしました', koukyuu.koukyuuList[index])
      } else {
        koukyuu.koukyuuList[index].koyou_keiyaku_id = null
        koukyuu.koukyuuList[index].mst_syuugyou_basyo_id = null
        koukyuu.koukyuuList[index].selectedSyuugyouBasyo = null
        koukyuu.koukyuuList[index].selectableTaniTypes = []
        koukyuu.koukyuuList[index].koukyuu_tani_type = null
        changeData()
        Logger.debug('公休' + index + 'の雇用契約IDと就業場所IDがクリアされました', koukyuu.koukyuuList[index])
      }
    }

    return {
      koukyuu,
      getError,
      onSave,
      dateFormat,
      deleteRow,
      addRow,
      changeData,
      OptionConst,
      'taniOption': OptionConst.SHINSEI_DIV_OPTION,
      isAll,
      getShinseiOption,
      changeKoyouKeiyakuTime
    }
  },
  components: {
    'koukyuu-all': KoukyuuAll,
    'koukyuu-not-all': KoukyuuNotAll,
    'koyouKeiyakuTimeSelect': KoyouKeiyakuTimeSelect
  }
})

</script>
<style lang="scss" scoped>
@import '@scss/components/shinsei-create.scss';
</style>
